/**
 * Filter-scoping helpers shared by the Billing Tracker filter bars
 * (Billing Tracker Approvals, Billing Manager, and any other page with the
 * same Plant + person dropdowns).
 *
 * When a specific person is picked in the "Submitted By" and/or "Regional
 * Manager" filter, the Plant dropdown should only offer the plant(s) that
 * person is assigned to — and if they have exactly one plant, it should be
 * selected automatically. `scopedPlantCodes` resolves the allowed set of
 * plant codes for the current selection.
 */

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function memberDisplayName(member) {
  return (
    member?.Title ||
    `${member?.FirstName || ''} ${member?.LastName || ''}`
  ).trim();
}

function memberPlantCodes(member) {
  const codes = Array.isArray(member?.plantCodes) ? member.plantCodes : [];
  return codes
    .map((code) => String(code || '').trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Given the team roster and the currently-selected person filters, return the
 * uppercased Set of plant codes the Plant dropdown should be limited to.
 *
 *  - Returns `null` when no person is selected (meaning: show ALL plants).
 *  - When only a submitter OR only a Regional Manager is selected, returns
 *    that person's assigned plant codes.
 *  - When BOTH are selected, returns the intersection (plants that both the
 *    submitter and the RM are assigned to).
 *  - A selected person with no assigned plants is treated as "no restriction
 *    from that person" (so we never blank out the dropdown unexpectedly);
 *    only an actual, non-empty intersection can narrow the list.
 *
 * @param {Array<object>} team    Roster from listTeam() (each row carries `plantCodes`).
 * @param {object} selection
 * @param {string} [selection.submitterKey]  The "Submitted By" value — a SubmitterId or lowercased name.
 * @param {string} [selection.rmName]         The "Regional Manager" value — a display name.
 * @returns {Set<string>|null}
 */
export function scopedPlantCodes(team, { submitterKey = '', rmName = '' } = {}) {
  const roster = Array.isArray(team) ? team : [];
  const sets = [];

  const submitter = String(submitterKey || '').trim();
  if (submitter) {
    const submitterLower = submitter.toLowerCase();
    const member = roster.find(
      (m) =>
        String(m?.id || '').trim() === submitter ||
        normalize(memberDisplayName(m)) === submitterLower,
    );
    const codes = member ? memberPlantCodes(member) : [];
    if (codes.length) sets.push(new Set(codes));
  }

  const rm = normalize(rmName);
  if (rm && rm !== 'unassigned') {
    const member = roster.find((m) => normalize(memberDisplayName(m)) === rm);
    const codes = member ? memberPlantCodes(member) : [];
    if (codes.length) sets.push(new Set(codes));
  }

  if (sets.length === 0) return null;

  let intersection = sets[0];
  for (let i = 1; i < sets.length; i += 1) {
    intersection = new Set(
      [...intersection].filter((code) => sets[i].has(code)),
    );
  }
  return intersection;
}

/**
 * The Team record a "Submitted By" filter value points at (matched by id
 * or display name, the same way scopedPlantCodes matches). Null when the
 * filter is empty or nobody matches. Added Oct 2026 so the Supplier
 * dropdown can follow the chosen Liaison Manager's supplier assignments.
 */
export function findSubmitterMember(team, submitterKey = '') {
  const submitter = String(submitterKey || '').trim();
  if (!submitter) return null;
  const submitterLower = submitter.toLowerCase();
  return (
    (Array.isArray(team) ? team : []).find(
      (m) =>
        String(m?.id || '').trim() === submitter ||
        normalize(memberDisplayName(m)) === submitterLower,
    ) || null
  );
}
